import { FORM_KEYS, UPLOAD_MANAGED_KEYS, verificationPropertiesParsingSchema } from './account-properties.schema';
export const isPlainObject = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
// The threshold lives in errors[], which the backend merges over its defaults
// by errorType (a full replace), so an override must carry the whole entry, not
// just the threshold. Match on errorType, the actual merge key — confidence
// isn't unique (a sibling entry can share it under a different errorType), so
// matching on it would collapse and drop those siblings.
const CROSSCHECK_THRESHOLD_ERROR_TYPE = 'CrosscheckIdentityError';
const CROSSCHECK_THRESHOLD_CONFIDENCE = 'id.crosscheck.identity.confidence';
const DEFAULT_CROSSCHECK_ERROR = {
    confidence: CROSSCHECK_THRESHOLD_CONFIDENCE,
    suggestion: 'Crosscheck identity below threshold',
    warning: true,
    errorType: CROSSCHECK_THRESHOLD_ERROR_TYPE,
    message: 'Crosscheck identity below threshold'
};
const isCrosscheckThresholdEntry = (e) => isPlainObject(e) && e['errorType'] === CROSSCHECK_THRESHOLD_ERROR_TYPE;
// The account's existing crosscheck entry, reused as the base so a threshold
// edit preserves its other fields.
export function crosscheckThresholdSource(originalProps) {
    if (!isPlainObject(originalProps) ||
        !Array.isArray(originalProps['errors'])) {
        return null;
    }
    const entry = originalProps['errors'].find(isCrosscheckThresholdEntry);
    return isPlainObject(entry) ? entry : null;
}
// Upsert the crosscheck entry into errors[], overriding only `threshold` on
// `base`. A null threshold inherits the default: errors[] is left untouched.
export function applyCrosscheckThreshold(payload, threshold, base = null) {
    if (threshold === null)
        return;
    const existing = Array.isArray(payload['errors'])
        ? payload['errors']
        : [];
    const rest = existing.filter((e) => !isCrosscheckThresholdEntry(e));
    const entry = {
        ...(base ?? DEFAULT_CROSSCHECK_ERROR),
        errorType: CROSSCHECK_THRESHOLD_ERROR_TYPE,
        threshold
    };
    payload['errors'] = [...rest, entry];
}
export const DEFAULT_FORM_STATE = {
    ssn: false,
    ssnPrivate: false,
    tinValidationEnabled: false,
    ein: '',
    ssnNameMatchThreshold: 70,
    ssnDobStrictMatch: false,
    cipScoringEnabled: false,
    dobVerification: false,
    includeMiddleName: false,
    rejectExpiredIds: false,
    disableScandit: false,
    aliasMatchEnabled: true,
    nameFuzzyMatchEnabled: true,
    nameBoundaryMatchEnabled: false,
    dobFuzzyMatch: true,
    rejectStandardLivenessSkipped: false,
    rejectEnhancedLivenessSkipped: false,
    liveness: '',
    aml: false,
    amlIdv: true,
    amlFuzziness: null,
    amlSearchProfile: '',
    amlScreeningConfig: '',
    amlMonitoringEnabled: false,
    amlMonitoringDays: '',
    amlMonitoringContactEmails: [],
    vidvEnabled: true,
    crosscheck: false,
    crosscheckModel: '',
    crosscheckStrictNameLinkage: false,
    crosscheckThreshold: null,
    trustTier: '',
    dlv_enabled: false,
    reverificationEnabled: true,
    inspectIP: false,
    idConstraintsAllow: [],
    idConstraintsDeny: [],
    smsCountryDenyList: '',
    smsCountryAllowList: [],
    tosConsentPageEnabled: false,
    tosPrivacyPolicyUrl: '',
    tosBiometricsPolicyUrl: '',
    tosConsentCopy: 'default',
    nistIal2ComplianceEnabled: false,
    nistIal2ProofingTemplateType: '',
    nistIal2CustomerName: '',
    digitalIdEnabled: false,
    digitalIdVerificationProfileId: '',
    notificationEmails: [],
    notificationJobCompleted: true,
    callbackURL: '',
    nowWebApp: {
        liveness: 'mouth',
        type: '',
        id: '',
        face: '',
        dob: '',
        ial2CrossCheckId: '',
        callbackURL: '',
        crossDeviceURL: '',
        enableIPAddress: false,
        enableDriversLicenseValidation: false,
        enablePhysicalAddress: false,
        enableDarkWeb: false,
        enableCrossCheck: false,
        enableIdDistance: false,
        enableEyeCheck: false,
        includeBarcode: false,
        includeBackId: false,
        crossDevice: false,
        crossDeviceQRCode: false,
        crossDeviceHandoff: false,
        crossDeviceSMS: false,
        theme: '',
        properties: ''
    },
    nowPath: '',
    advancedJson: ''
};
/** Sets the crosscheck model; moving to or from Match clears Strict Name Linkage. */
export const withCrosscheckModel = (state, crosscheckModel) => ({
    ...state,
    crosscheckModel,
    crosscheckStrictNameLinkage: !!state.crosscheckModel &&
        !!crosscheckModel &&
        state.crosscheckStrictNameLinkage
});
const mapConstraints = (arr) => (arr ?? [])
    .filter((c) => c.country !== undefined || c.type !== undefined)
    .map((c) => ({ country: c.country, type: c.type }));
export function parsePropsToFormState(raw) {
    if (!raw)
        return { state: { ...DEFAULT_FORM_STATE }, schemaWarning: null };
    const parsed = raw;
    const schemaResult = verificationPropertiesParsingSchema.safeParse(parsed);
    let schemaWarning = null;
    if (schemaResult.error) {
        schemaWarning = schemaResult.error.issues
            .map((i) => `${i.path.join('.')}: ${i.message}`)
            .join('\n');
        console.warn('[account-properties] schema validation warning:', schemaWarning);
    }
    const advanced = {};
    for (const [key, value] of Object.entries(parsed)) {
        if (!FORM_KEYS.has(key) && !UPLOAD_MANAGED_KEYS.has(key)) {
            advanced[key] = value;
        }
    }
    // Lift the threshold into its own field and drop that entry from the Advanced
    // JSON so it isn't owned twice. Only manage a numeric threshold; leave anything
    // else in Advanced JSON untouched.
    let crosscheckThreshold = null;
    if (Array.isArray(advanced['errors'])) {
        const errors = advanced['errors'];
        const entry = errors.find(isCrosscheckThresholdEntry);
        if (isPlainObject(entry) && typeof entry['threshold'] === 'number') {
            crosscheckThreshold = entry['threshold'];
            const rest = errors.filter((e) => !isCrosscheckThresholdEntry(e));
            if (rest.length)
                advanced['errors'] = rest;
            else
                delete advanced['errors'];
        }
    }
    const amlMon = parsed.amlMonitoring ?? {};
    const idConstr = parsed.idConstraints ?? {};
    const notifs = parsed.notifications ?? {};
    const tos = parsed.tosOptions ?? {};
    const nist = parsed.nist ?? {};
    const digitalId = parsed.digitalIdOptions ?? {};
    const nowWebApp = (parsed.nowWebApp ?? {});
    const now = parsed.now ?? {};
    const state = {
        ssn: parsed.ssn ?? false,
        ssnPrivate: parsed.ssnPrivate ?? false,
        tinValidationEnabled: parsed.tinValidationEnabled ?? false,
        ein: parsed.ein ?? '',
        ssnNameMatchThreshold: parsed.ssnNameMatchThreshold ?? 70,
        ssnDobStrictMatch: parsed.ssnDobStrictMatch ?? false,
        cipScoringEnabled: parsed.cipScoringEnabled ?? false,
        dobVerification: parsed.dobVerification ?? false,
        includeMiddleName: parsed.includeMiddleName ?? false,
        rejectExpiredIds: parsed.rejectExpiredIds ?? false,
        disableScandit: parsed.disableScandit ?? false,
        aliasMatchEnabled: parsed.aliasMatchEnabled ?? true,
        nameFuzzyMatchEnabled: parsed.nameFuzzyMatchEnabled ?? true,
        nameBoundaryMatchEnabled: parsed.nameBoundaryMatchEnabled ?? false,
        dobFuzzyMatch: parsed.dobFuzzyMatch ?? true,
        rejectStandardLivenessSkipped: parsed.rejectStandardLivenessSkipped ?? false,
        rejectEnhancedLivenessSkipped: parsed.rejectEnhancedLivenessSkipped ?? false,
        liveness: parsed.liveness ?? '',
        aml: parsed.aml ?? false,
        amlIdv: parsed.amlIdv ?? true,
        amlFuzziness: 'amlFuzziness' in parsed ? (parsed.amlFuzziness ?? null) : null,
        amlSearchProfile: parsed.amlSearchProfile ?? '',
        amlScreeningConfig: parsed.amlScreeningConfig ?? '',
        amlMonitoringEnabled: amlMon.enabled ?? false,
        amlMonitoringDays: amlMon.days === null || amlMon.days === undefined
            ? ''
            : String(amlMon.days),
        amlMonitoringContactEmails: amlMon.contactEmails ?? [],
        vidvEnabled: parsed.vidvEnabled ?? true,
        crosscheck: parsed.crosscheck ?? false,
        crosscheckModel: parsed.crosscheckConfig?.model ?? '',
        crosscheckStrictNameLinkage: parsed.crosscheckConfig?.strictNameLinkage ?? false,
        crosscheckThreshold,
        trustTier: parsed.trustTier ?? '',
        dlv_enabled: parsed.dlv_enabled ?? false,
        reverificationEnabled: parsed.reverificationEnabled ?? true,
        inspectIP: parsed.inspectIP ?? false,
        idConstraintsAllow: mapConstraints(idConstr.allow),
        idConstraintsDeny: mapConstraints(idConstr.deny),
        smsCountryDenyList: (parsed.smsCountryDenyList ?? []).join(', '),
        smsCountryAllowList: parsed.smsCountryAllowList ?? [],
        tosConsentPageEnabled: tos.consentPageEnabled ?? false,
        tosPrivacyPolicyUrl: tos.privacyPolicyUrl ?? '',
        tosBiometricsPolicyUrl: tos.biometricsPolicyUrl ?? '',
        tosConsentCopy: tos.consentCopy ??
            // Deprecated flag, read until legacy accounts are migrated.
            (tos.internationalConsentEnabled ? 'international' : 'default'),
        nistIal2ComplianceEnabled: nist.ial2ComplianceEnabled ?? false,
        nistIal2ProofingTemplateType: nist.ial2ProofingTemplate?.type ?? '',
        nistIal2CustomerName: nist.ial2ProofingTemplate?.customerName ?? '',
        digitalIdEnabled: digitalId.enabled ?? false,
        digitalIdVerificationProfileId: digitalId.verificationProfileId ?? '',
        notificationEmails: notifs.emails ?? [],
        notificationJobCompleted: notifs.job?.completed ?? true,
        callbackURL: parsed.callbackURL ?? '',
        nowWebApp: {
            liveness: nowWebApp.liveness ?? 'mouth',
            type: nowWebApp.type ?? '',
            id: nowWebApp.id ?? '',
            face: nowWebApp.face ?? '',
            dob: nowWebApp.dob ?? '',
            ial2CrossCheckId: nowWebApp.ial2CrossCheckId ?? '',
            callbackURL: nowWebApp.callbackURL ?? '',
            crossDeviceURL: nowWebApp.crossDeviceURL ?? '',
            enableIPAddress: nowWebApp.enableIPAddress ?? false,
            enableDriversLicenseValidation: nowWebApp.enableDriversLicenseValidation ?? false,
            enablePhysicalAddress: nowWebApp.enablePhysicalAddress ?? false,
            enableDarkWeb: nowWebApp.enableDarkWeb ?? false,
            enableCrossCheck: nowWebApp.enableCrossCheck ?? false,
            enableIdDistance: nowWebApp.enableIdDistance ?? false,
            enableEyeCheck: nowWebApp.enableEyeCheck ?? false,
            includeBarcode: nowWebApp.includeBarcode ?? false,
            includeBackId: nowWebApp.includeBackId ?? false,
            crossDevice: nowWebApp.crossDevice ?? false,
            crossDeviceQRCode: nowWebApp.crossDeviceQRCode ?? false,
            crossDeviceHandoff: nowWebApp.crossDeviceHandoff ?? false,
            crossDeviceSMS: nowWebApp.crossDeviceSMS ?? false,
            theme: nowWebApp.theme ? JSON.stringify(nowWebApp.theme, null, 2) : '',
            properties: nowWebApp.properties
                ? JSON.stringify(nowWebApp.properties, null, 2)
                : ''
        },
        nowPath: now.path ?? '',
        advancedJson: Object.keys(advanced).length > 0 ? JSON.stringify(advanced, null, 2) : ''
    };
    return { state, schemaWarning };
}
const csv = (str) => str
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
const buildConstraints = (rules) => {
    const mapped = rules
        .filter((r) => r.country?.trim() ?? r.type)
        .map((r) => {
        const c = {};
        if (r.country?.trim())
            c.country = r.country.trim().toUpperCase();
        if (r.type)
            c.type = r.type;
        return c;
    });
    return mapped.length > 0 ? mapped : undefined;
};
// nowWebApp fields copied through to the payload only when truthy (sparse).
// Excludes `liveness`/`enableEyeCheck` (always set) and `theme`/`properties`
// (parsed from JSON separately).
const NOW_WEB_APP_COPY_KEYS = [
    'type',
    'id',
    'face',
    'dob',
    'ial2CrossCheckId',
    'callbackURL',
    'crossDeviceURL',
    'enableIPAddress',
    'enableDriversLicenseValidation',
    'enablePhysicalAddress',
    'enableDarkWeb',
    'enableCrossCheck',
    'enableIdDistance',
    'includeBarcode',
    'includeBackId',
    'crossDevice',
    'crossDeviceQRCode',
    'crossDeviceHandoff',
    'crossDeviceSMS'
];
export function buildPropsFromFormState(state) {
    let advanced = {};
    if (state.advancedJson.trim()) {
        try {
            advanced = JSON.parse(state.advancedJson);
        }
        catch {
            throw new Error('Advanced JSON section contains invalid JSON');
        }
    }
    const allow = buildConstraints(state.idConstraintsAllow);
    const deny = buildConstraints(state.idConstraintsDeny);
    const props = {
        ssn: state.ssn,
        ssnPrivate: state.ssnPrivate,
        tinValidationEnabled: state.tinValidationEnabled,
        ssnNameMatchThreshold: state.ssnNameMatchThreshold ?? 70,
        ssnDobStrictMatch: state.ssnDobStrictMatch,
        cipScoringEnabled: state.cipScoringEnabled,
        dobVerification: state.dobVerification,
        includeMiddleName: state.includeMiddleName,
        rejectExpiredIds: state.rejectExpiredIds,
        aliasMatchEnabled: state.aliasMatchEnabled,
        nameFuzzyMatchEnabled: state.nameFuzzyMatchEnabled,
        nameBoundaryMatchEnabled: state.nameBoundaryMatchEnabled,
        dobFuzzyMatch: state.dobFuzzyMatch,
        rejectStandardLivenessSkipped: state.rejectStandardLivenessSkipped,
        rejectEnhancedLivenessSkipped: state.rejectEnhancedLivenessSkipped,
        aml: state.aml,
        amlIdv: state.amlIdv,
        amlMonitoring: {
            enabled: state.amlMonitoringEnabled,
            days: state.amlMonitoringDays ? Number(state.amlMonitoringDays) : null,
            contactEmails: state.amlMonitoringContactEmails
        },
        crosscheck: state.crosscheck,
        dlv_enabled: state.dlv_enabled,
        reverificationEnabled: state.reverificationEnabled,
        inspectIP: state.inspectIP,
        smsCountryDenyList: csv(state.smsCountryDenyList),
        notifications: {
            emails: state.notificationEmails,
            job: { completed: state.notificationJobCompleted }
        }
    };
    if (state.ein)
        props.ein = state.ein;
    if (state.liveness)
        props.liveness = state.liveness;
    const smsCountryAllowList = state.smsCountryAllowList.filter(Boolean);
    if (smsCountryAllowList.length)
        props.smsCountryAllowList = smsCountryAllowList;
    if (state.amlFuzziness !== null)
        props.amlFuzziness = state.amlFuzziness;
    if (state.amlSearchProfile)
        props.amlSearchProfile = state.amlSearchProfile;
    if (state.amlScreeningConfig)
        props.amlScreeningConfig = state.amlScreeningConfig;
    if (state.callbackURL)
        props.callbackURL = state.callbackURL;
    // Always emit the object so a diff against a set baseline unsets just the
    // `model` key (nested deletion) rather than dropping the whole
    // `crosscheckConfig` block and any sibling fields with it.
    const crosscheckConfig = {};
    if (state.crosscheckModel)
        crosscheckConfig.model = state.crosscheckModel;
    // Mirrored even without a model so a cleared leftover key is deleted on save.
    if (state.crosscheckStrictNameLinkage)
        crosscheckConfig.strictNameLinkage = true;
    props.crosscheckConfig = crosscheckConfig;
    // Root-level scalar: setting it writes the key, leaving it on Default omits it
    // so the diff deletes the root key (nothing to preserve inside it).
    if (state.trustTier)
        props.trustTier = state.trustTier;
    // Written only when disabled, so toggling it off deletes the key and the
    // account inherits Scandit-enabled.
    if (state.disableScandit)
        props.disableScandit = true;
    // Persist only the disabled state; an absent key inherits the default.
    if (!state.vidvEnabled)
        props.vidvEnabled = false;
    if (allow ?? deny) {
        props.idConstraints = { allow, deny };
    }
    if (state.tosConsentPageEnabled ||
        state.tosPrivacyPolicyUrl ||
        state.tosBiometricsPolicyUrl ||
        state.tosConsentCopy !== 'default') {
        // Always explicit: the backend carries an absent consentCopy forward, so
        // choosing Default must send it rather than drop the key.
        const tosOptions = {
            consentCopy: state.tosConsentCopy,
            consentPageEnabled: state.tosConsentPageEnabled
        };
        if (state.tosPrivacyPolicyUrl)
            tosOptions.privacyPolicyUrl = state.tosPrivacyPolicyUrl;
        if (state.tosBiometricsPolicyUrl)
            tosOptions.biometricsPolicyUrl = state.tosBiometricsPolicyUrl;
        props.tosOptions = tosOptions;
    }
    if (state.nistIal2ComplianceEnabled || state.nistIal2ProofingTemplateType) {
        const nist = {
            ial2ComplianceEnabled: state.nistIal2ComplianceEnabled
        };
        if (state.nistIal2ProofingTemplateType) {
            const template = {
                type: state.nistIal2ProofingTemplateType
            };
            if (state.nistIal2CustomerName)
                template.customerName = state.nistIal2CustomerName;
            nist.ial2ProofingTemplate = template;
        }
        props.nist = nist;
    }
    if (state.digitalIdEnabled || state.digitalIdVerificationProfileId) {
        const digitalIdOptions = {
            enabled: state.digitalIdEnabled
        };
        if (state.digitalIdVerificationProfileId) {
            digitalIdOptions.verificationProfileId =
                state.digitalIdVerificationProfileId;
        }
        props.digitalIdOptions = digitalIdOptions;
    }
    const nwa = state.nowWebApp;
    const nowWebAppObj = {
        liveness: nwa.liveness || 'mouth',
        enableEyeCheck: nwa.enableEyeCheck
    };
    // Copy each optional field through only when truthy, so empty/false values
    // stay absent (sparse). Booleans collapse to `true`; strings copy their value.
    for (const key of NOW_WEB_APP_COPY_KEYS) {
        const value = nwa[key];
        // Correlated-union assignment across a keyof union needs a cast here.
        if (value)
            nowWebAppObj[key] = value;
    }
    if (nwa.theme.trim()) {
        try {
            nowWebAppObj.theme = JSON.parse(nwa.theme);
        }
        catch {
            throw new Error('Invite Link Theme contains invalid JSON');
        }
    }
    if (nwa.properties.trim()) {
        try {
            nowWebAppObj.properties = JSON.parse(nwa.properties);
        }
        catch {
            throw new Error('Invite Link Properties contains invalid JSON');
        }
    }
    props.nowWebApp = nowWebAppObj;
    if (state.nowPath)
        props.now = { path: state.nowPath };
    return { ...props, ...advanced };
}
/** Drops keys owned by their own uploader so editor saves never send them. */
export const withoutUploadManagedKeys = (props) => Object.fromEntries(Object.entries(props).filter(([key]) => !UPLOAD_MANAGED_KEYS.has(key)));
